// ============================================
// DeltaVolumePro.jsx — "Volume Delta Pro" oscillator subgraph.
//
// Port of Levi's Tuned-style script: per-bar (or rolling-sum) volume delta,
// coins or notional units, optional SMA smoothing, rolling ±1/2/3σ bands,
// and a 10-color dynamic palette keyed to σ multiples (0.5/1/2/2.8) — or
// fixed pos/neg colors.
//
// CONTRACT: identical scroll-sync discipline to CumulativeDeltaSubgraph —
// viewport math comes from the SAME refs the main canvas reads, inside rAF,
// never from React state (the "candles pop at scroll boundaries" lesson).
// ============================================
import { useEffect, useRef, useState } from 'react'
import { FIXED_OVERLAY_WIDTH, ROW_HEIGHT, formatNumber } from '../utils/chartUtils'
import { placeCrosshairs, registerCrosshairEl, CROSSHAIR_PANE_STYLE } from '../utils/crosshair'
import { setCtxFont } from '../utils/canvasFont'
import { requestFrame, cancelFrame, FRAME_LAYER } from '../utils/frameDriver'
import { subgraphX } from '../utils/subgraphX'

// exact palette from the source script (indexes 0-4 positive, 5-9 negative)
const DYN_PALETTE = [
  '#1dcaff', '#1e9b25', '#00ff00', '#00ff00', '#33ff00',
  '#e65100', '#771515', '#ff0000', '#ff0000', '#ff0000',
]

export const DVP_DEFAULTS = {
  deltaMode: 'perbar',      // 'perbar' | 'rolling'
  rollingLen: 50,
  unitMode: 'coins',        // 'coins' | 'usd'
  colorMode: 'fixed',       // 'fixed' | 'dynamic'
  smoothLen: 0,             // 0 = off
  volLookback: 50,
  showStddev: true,
  fillBands: true,
  posCol: '#64ffda',      // sigma BANDS (unchanged for now)
  negCol: '#f44336',
  // Column (bar) colors are their own knobs so the bands can keep their
  // palette while the bars follow the user's bull/bear choice.
  colPos: '#64ffda',
  colNeg: '#f44336',
}

// σ index per the script: 0..4 positive tiers, 5..9 negative tiers
export function paletteIndex(v, sa) {
  const c1 = 0.5 * sa, c2 = 1.0 * sa, c3 = 2.0 * sa, c4 = 2.8 * sa
  if (v >= c4) return 4
  if (v >= c3) return 3
  if (v >= c2) return 2
  if (v >= c1) return 1
  if (v >= 0) return 0
  if (v > -c1) return 5
  if (v > -c2) return 6
  if (v > -c3) return 7
  if (v > -c4) return 8
  return 9
}

// Rolling delta series + rolling σ, computed once per commit (O(n)).
export function computeDvp(candles, cfg) {
  const n = candles.length
  const out = { v: new Float64Array(n), sigma: new Float64Array(n) }
  if (!n) return out
  // Flow extraction ported from useCumulativeDelta's candleFlow VERBATIM:
  // candles carry NO buyVolume/sellVolume fields. Live/unpacked candles
  // keep per-level flow in the _liveLevels Map; packed candles pre-sum it
  // as _nativeTotalVol/_nativeDelta; pure-kline historical candles have no
  // flow at all (zeros — same domain CVD anchors around). Reading fictional
  // field names produced an all-zero series: a subgraph that "rendered"
  // nothing but a zero line.
  // isHistorical means "never rebuild me from the live trade buffer" — it is
  // NOT "I have no flow". The OM footprint upgrade stamps isHistorical:true
  // on candles it packs WITH full _native/_nativeDelta (its own header says
  // so), and gating on the flag zeroed every upgraded candle: DeltaPro
  // reached back a tenth as far as CVD over data that was sitting right
  // there. Read whatever flow fields exist; gate on nothing.
  const raw = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const c = candles[i]
    let d = 0
    if (c) {
      if (c._liveLevels && c._liveLevels.size) {
        let buy = 0, sell = 0
        for (const lvl of c._liveLevels.values()) { buy += lvl.buy || 0; sell += lvl.sell || 0 }
        d = buy - sell
      } else if (c._native) {
        d = c._nativeDelta || 0
      }
    }
    if (cfg.unitMode === 'usd') d *= (c?.close_mcap || 0)
    raw[i] = d
  }
  // optional SMA smoothing of the raw per-bar delta
  let base = raw
  if (cfg.smoothLen > 1) {
    base = new Float64Array(n)
    let s = 0
    for (let i = 0; i < n; i++) {
      s += raw[i]
      if (i >= cfg.smoothLen) s -= raw[i - cfg.smoothLen]
      base[i] = s / Math.min(i + 1, cfg.smoothLen)
    }
  }
  // per-bar vs rolling sum
  if (cfg.deltaMode === 'rolling') {
    let s = 0
    for (let i = 0; i < n; i++) {
      s += base[i]
      if (i >= cfg.rollingLen) s -= base[i - cfg.rollingLen]
      out.v[i] = s
    }
  } else {
    out.v.set(base)
  }
  // rolling stddev of the PLOTTED series (matches stddev(deltaRef, lookback))
  const L = Math.max(2, cfg.volLookback | 0)
  let s = 0, s2 = 0
  for (let i = 0; i < n; i++) {
    const v = out.v[i]
    s += v; s2 += v * v
    if (i >= L) { const o = out.v[i - L]; s -= o; s2 -= o * o }
    const m = Math.min(i + 1, L)
    const mean = s / m
    const va = Math.max(0, s2 / m - mean * mean)
    out.sigma[i] = Math.sqrt(va)
  }
  return out
}

const hexA = (hex, a) => {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${a})`
}

// Signed whole-number abbreviation for the axis gutter (see CVD's formatTag).
// Module scope: shared by the canvas axis/chip draw AND the DOM crosshair
// chip's pointermove handler — one formatter, both surfaces read identically.
const fmtDelta = (v) => {
  if (!v) return '0'
  const a = Math.abs(v)
  const t = a >= 1e9 ? Math.round(a / 1e9) + 'B'
          : a >= 1e6 ? Math.round(a / 1e6) + 'M'
          : a >= 1e3 ? Math.round(a / 1e3) + 'K'
          : String(Math.round(a))
  return (v > 0 ? '+' : '-') + t
}

const DVP_SCALE_KEY = 'solcheddar:dvpXScale'
// Discrete stops — same set as the CVD pane, so the two subgraphs are
// configured in the same vocabulary rather than each inventing one.
const DVP_SCALES = [1, 2, 3, 5, 8]

export default function DeltaVolumePro({
  candles,
  viewEndIndexRef,
  viewWindowSizeRef,
  containerWidthRef,
  paintNowRef,        // (resize-flash) chart's ref: set to a synchronous paint for its resize callback
  axisWidth,          // live y-axis width from the chart (falls back to the constant)
  fractionalOffsetPxRef,
  frameRef,           // (subx) the chart's lastFrameRef — painted X geometry
  mouseXRef,
  rowHeightRef,       // the chart's dynamicRowHeightRef — LIVE row height for the h-highlight strip
  onCursorXChange,    // (x: number) => void — syncs the main chart's DOM crosshair; -1 hides
  config = DVP_DEFAULTS,
  height = 110,
}) {
  const plotRef = useRef(null)
  const rafRef = useRef(0)
  // Horizontal crosshair is DOM, not canvas — the chart's own mechanism.
  // A canvas imitation drifted twice (strip used the static ROW_HEIGHT
  // constant while the chart reads dynamicRowHeightRef.current; chip text
  // hand-rebuilt CSS from a snapshot). Same classNames as the chart
  // (.chart-h-highlight / .price-label / .price-label-text) inherit the
  // EXACT live stylesheet — nothing to drift by construction. Positioned
  // imperatively in pointermove, the same 60fps no-state pattern as
  // updateCrosshair and the chart's own cursorLabelRef.
  const hlRef = useRef(null)      // horizontal highlight strip
  const chipRef = useRef(null)    // gutter value label (outer .price-label)
  // Scale snapshot for the chip value — written by the draw loop each frame,
  // read by the pointer handler (CVD's yScaleRef pattern exactly).
  const yScaleRef = useRef({ mx: 1, mid: 1 })
  // X geometry published each frame so the REVERSE-direction pointer handler
  // can map a pixel in THIS pane back to a candle, then forward to the main
  // chart's pixel for that same candle. Without it, hovering here at xS>1
  // would drive the chart's crosshair onto a different bar than the one
  // under your cursor.
  const xGeomRef = useRef({ cw: 1, frac: 0, frontAdj: 0, endIdx: 0, mainCw: 1 })
  // THIS pane's own cursor x, in its own pixels. Needed because mouseXRef is
  // a MAIN-CHART coordinate and goes to -1 whenever the hovered bar is
  // outside the chart's window — which happens constantly at xS>1, since this
  // pane shows far more history than the chart does. Without a local copy the
  // line would vanish under your own cursor.
  const selfCursorXRef = useRef(-1)
  const xhairRef = useRef(null)
  useEffect(() => registerCrosshairEl(mouseXRef, xhairRef.current), [mouseXRef])

  // ─── X-scale decoupling ───
  // Sticky and GLOBAL, separate from the CVD pane's setting: the two panes
  // answer different questions and you may well want per-bar delta at 1×
  // while the cumulative line runs at 5×.
  const [xScale, setXScale] = useState(() => {
    try {
      const v = Number(localStorage.getItem(DVP_SCALE_KEY))
      return DVP_SCALES.includes(v) ? v : 1
    } catch { return 1 }
  })
  const [scaleUiHot, setScaleUiHot] = useState(false)
  const xScaleRef = useRef(xScale)
  useEffect(() => {
    xScaleRef.current = xScale
    try { localStorage.setItem(DVP_SCALE_KEY, String(xScale)) } catch {}
  }, [xScale])
  const seriesRef = useRef({ v: new Float64Array(0), sigma: new Float64Array(0) })
  const cfgRef = useRef(config)
  cfgRef.current = { ...DVP_DEFAULTS, ...config }

  // chartData.candles MUTATES in place across commits — identity never
  // changes, and worse, deep merges upgrade MID-ARRAY candles (adding
  // _nativeDelta to historical klines) without touching length or the
  // newest candle, so any staleness fingerprint cheap enough to check is
  // also cheap enough to fool: the first cut froze a pre-upgrade series
  // and the subgraph reached back shorter than CVD. The compute is three
  // O(n) passes over ~500 candles — microseconds — so it simply runs
  // every frame. No cache, no key, nothing to go stale.

  useEffect(() => {
    const cv = plotRef.current
    if (!cv) return
    const ctx = cv.getContext('2d')
    let live = true
    const draw = () => {
      if (!live) return
      rafRef.current = requestFrame(draw, FRAME_LAYER)
      const W = containerWidthRef.current || cv.parentElement?.clientWidth || 300
      const H = height
      const dpr = window.devicePixelRatio || 1
      if (cv.width !== Math.round(W * dpr) || cv.height !== Math.round(H * dpr)) {
        cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr)
        cv.style.width = W + 'px'; cv.style.height = H + 'px'
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, W, H)
      // (subx) X comes from the chart's PAINTED frame — same object, same
      // frame (this loop runs at FRAME_LAYER, after the chart's FRAME_DRAW),
      // with the chart's X camera applied. See utils/subgraphX.js.
      const f = frameRef ? frameRef.current : null
      const cs = (f && f.candles) || candles || []
      const cfgNow = cfgRef.current
      seriesRef.current = computeDvp(cs, cfgNow)
      const S = seriesRef.current
      const n = Math.min(cs.length, S.v.length)
      if (!n) return
      const plotW = Math.max(50, W - (axisWidth || FIXED_OVERLAY_WIDTH))   // gutter only; X anchors on the chart's plotWidth
      const g = subgraphX(f, xScaleRef.current || 1)
      if (!g) return
      const startIdx = g.start
      const dataEnd = Math.min(g.end, n - 1)
      const cw = g.cw
      const X = g.xLeft
      const cfg = cfgNow
      // autoscale on visible |v| and 3σ
      // Scale fits the PLOTTED BARS in the viewport only. Including 3-sigma
      // here looked windowed but wasn't in spirit: sigma carries a 50-bar
      // memory, so for 50 bars after any monster delta (the launch candle)
      // the band ceiling stayed enormous while visible bars were tiny —
      // everything flattened as if scaled to all history. Standard charting
      // rule instead: bars own the scale; bands clip at the pane edge when
      // their tails run past it.
      let mx = 1e-9
      for (let i = startIdx; i <= dataEnd; i++) {
        const a = Math.abs(S.v[i]); if (a > mx) mx = a
      }
      // 12% headroom: bars and sigma bands shouldn't touch the pane edges,
      // and the +max/-max labels need room to sit inside the gutter.
      mx *= 1.12
      const mid = H / 2
      yScaleRef.current = { mx, mid }   // chip value math in pointermove reads this
      xGeomRef.current = g
      const yOf = (v) => mid - (v / mx) * (mid - 6)
      // σ bands (drawn from per-bar rolling σ — bands are a timeseries)
      if (cfg.showStddev) {
        const band = (k0, k1, col, alpha) => {
          ctx.beginPath()
          for (let i = startIdx; i <= dataEnd; i++) {
            const x = X(i) + cw / 2
            const y = yOf(k0 * S.sigma[i])
            i === startIdx ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
          }
          for (let i = dataEnd; i >= startIdx; i--) {
            const x = X(i) + cw / 2
            ctx.lineTo(x, yOf(k1 * S.sigma[i]))
          }
          ctx.closePath()
          ctx.fillStyle = hexA(col, alpha)
          ctx.fill()
        }
        if (cfg.fillBands) {
          band(1, 2, cfg.posCol, 0.08); band(2, 3, cfg.posCol, 0.18)
          band(-1, -2, cfg.negCol, 0.08); band(-2, -3, cfg.negCol, 0.18)
        } else {
          ctx.lineWidth = 1
          for (const k of [1, 2, 3]) {
            for (const sgn of [1, -1]) {
              ctx.strokeStyle = hexA(sgn > 0 ? cfg.posCol : cfg.negCol, 0.4)
              ctx.beginPath()
              for (let i = startIdx; i <= dataEnd; i++) {
                const x = X(i) + cw / 2
                const y = yOf(sgn * k * S.sigma[i])
                i === startIdx ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
              }
              ctx.stroke()
            }
          }
        }
      }
      // zero line
      ctx.strokeStyle = 'rgba(150,160,190,0.35)'
      ctx.lineWidth = 1
      ctx.setLineDash([2, 3])
      ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(plotW, mid); ctx.stroke()
      ctx.setLineDash([])
      // delta bars
      const bw = Math.max(1, cw * 0.72)
      for (let i = startIdx; i <= dataEnd; i++) {
        const v = S.v[i]
        const x = X(i) + (cw - bw) / 2
        const y = yOf(v)
        ctx.fillStyle = cfg.colorMode === 'dynamic'
          ? DYN_PALETTE[paletteIndex(v, S.sigma[i] > 0 ? S.sigma[i] : 1)]
          : (v >= 0 ? (cfg.colPos || cfg.posCol) : (cfg.colNeg || cfg.negCol))
        v >= 0 ? ctx.fillRect(x, y, bw, mid - y) : ctx.fillRect(x, mid, bw, y - mid)
      }
      // ── Axis gutter (the reserved FIXED_OVERLAY_WIDTH column) ──
      // Same gutter as the chart so columns align; OWN units (delta, not
      // price — matching the price scale would be meaningless). Three scale
      // labels + a live chip on the current bar's value, CVD-style.
      // Same abbreviation as the main chart's axis (and CVD's formatTag):
      // chartUtils.formatNumber, signed.
      // Whole numbers only (see CVD's formatTag) — matched deliberately so
      // both subgraph gutters read identically. Hoisted to module scope
      // (fmtDelta) so the DOM crosshair chip shares it verbatim.
      const fmtD = fmtDelta
      // Axis labels match the MAIN CHART's price axis exactly (labelFont in
      // FootprintChartCanvas): same family, same 500 weight, same size — the
      // gutter reads as one continuous axis down the whole instrument
      // instead of three components each with an opinion.
      // TRUE main-chart axis style: the price ladder is DOM (.y-axis-row)
      // at 14px "Nova Square" 400 / #646464 — not the 11px line-tag font
      // matched here previously. Zero label removed: the dashed line says it.
      setCtxFont(ctx, '400 14px "Nova Square", system-ui, sans-serif')   // (perf3)
      ctx.textAlign = 'center'          // = .y-axis-row's justify-content: center
      ctx.fillStyle = '#646464'
      const axisMidX = plotW + (W - plotW) / 2
      // Evenly spaced ticks across the pane (skipping the zero line, which
      // the dashed baseline already marks) — the axis had only the two
      // extremes, so mid-range values had nothing to read against.
      const N_TICKS = 5
      for (let t = 0; t < N_TICKS; t++) {
        const frac = t / (N_TICKS - 1)          // 0 = top, 1 = bottom
        const v = mx - frac * (2 * mx)
        if (Math.abs(v) < mx * 0.05) continue    // skip the zero row
        const y = Math.max(11, Math.min(H - 4, frac * H + 4))
        ctx.fillText(fmtD(v), axisMidX, y)
      }
      const lastV = S.v[dataEnd]
      if (Number.isFinite(lastV)) {
        const cyRaw = yOf(lastV)
        const cy = Math.max(8, Math.min(H - 8, cyRaw))
        // Dashed line at the current value across the pane — the chart's
        // price-line grammar, so the chip has something to point at.
        if (cyRaw >= 0 && cyRaw <= H) {
          ctx.save()
          ctx.strokeStyle = lastV >= 0 ? 'rgba(38,166,154,0.55)' : 'rgba(239,83,80,0.55)'
          ctx.lineWidth = 1
          ctx.setLineDash([4, 4])
          ctx.beginPath()
          ctx.moveTo(0, Math.round(cyRaw) + 0.5)
          ctx.lineTo(plotW, Math.round(cyRaw) + 0.5)
          ctx.stroke()
          ctx.restore()
        }
        ctx.fillStyle = lastV >= 0 ? 'rgba(38,166,154,0.92)' : 'rgba(239,83,80,0.92)'
        ctx.fillRect(plotW + 1, cy - 8, W - plotW - 2, 16)
        ctx.fillStyle = '#0b0d12'
        setCtxFont(ctx, '600 14px "Nova Square", system-ui, sans-serif')   // (perf3)
        // CENTERED in the chip: left-aligned text drifted as the value's
        // digit count changed, so the number appeared to wander frame to
        // frame. Fixed chip + centered text = it only moves vertically.
        // 'middle' baseline at exact cy — the old 'alphabetic' baseline with
        // a +4 nudge sat the glyphs high in the box.
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(fmtD(lastV), plotW + 1 + (W - plotW - 2) / 2, cy)
        ctx.textBaseline = 'alphabetic'
      }

      // Vertical crosshair: DOM line (xhairRef), moved by utils/crosshair
      // placeCrosshairs in the same call as the chart's — not painted here,
      // where it trailed by a frame or more.
    }
    rafRef.current = requestFrame(draw, FRAME_LAYER)
    // (resize-flash) The chart's resize callback paints this pane in the
    // same frame as itself: drop the queued frame, draw now (draw re-arms).
    if (paintNowRef) paintNowRef.current = () => { if (!live) return; cancelFrame(rafRef.current); draw() }
    return () => {
      live = false; cancelFrame(rafRef.current)
      if (paintNowRef) paintNowRef.current = null
    }
  }, [candles, height])

  // X-wheel ownership stays on FootprintChartCanvas. Its native body listener
  // captures the cursor anchor and drives the shared Animator; handling wheel
  // here too would apply a second, unanchored state update before that anchor
  // is solved and makes this pane jump against the footprint.
  const wrapRef = useRef(null)

  // Reverse direction, mirrored from CVD's onPlotPointerMove: hovering DVP
  // writes the SAME mouseXRef the main canvas re-reads every rAF, and drives
  // the chart's DOM crosshair through onCursorXChange — the line continues
  // up out of this pane exactly as it continues down into it. Over the axis
  // gutter (x > plotW) it hides, matching the main chart's outOfChart check.
  const hideHorizontal = () => {
    if (hlRef.current) hlRef.current.style.display = 'none'
    if (chipRef.current) chipRef.current.style.display = 'none'
  }
  const onPointerMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect()
    if (!rect) return
    const x = e.clientX - rect.left
    const plotW = Math.max(50, (containerWidthRef.current || rect.width) - (axisWidth || FIXED_OVERLAY_WIDTH))
    selfCursorXRef.current = x <= plotW ? x : -1
    if (x > plotW) {
      // Over the gutter — the chart's isOverYAxis check hides BOTH the
      // vertical line and the label/highlight together, every time.
      if (mouseXRef.current !== -1) { mouseXRef.current = -1; if (onCursorXChange) onCursorXChange(-1); else placeCrosshairs(mouseXRef, -1) }
      hideHorizontal()
      return
    }
    // Same screen x as the chart at every xS (utils/crosshair): the raw x IS
    // the shared crosshair position.
    const mainX = x
    mouseXRef.current = mainX
    if (onCursorXChange) onCursorXChange(mainX)
    else placeCrosshairs(mouseXRef, mainX)
    // Horizontal: position the DOM strip + chip at cursor Y — same
    // imperative pattern as the chart's own cursor label (no state, 60fps).
    const y = e.clientY - rect.top
    const hl = hlRef.current
    if (hl) {
      // Height from the chart's LIVE row height ref — the constant is only
      // the fallback when the ref isn't wired.
      const RH = rowHeightRef?.current || ROW_HEIGHT
      hl.style.top = `${y}px`
      hl.style.left = '0px'
      hl.style.width = `${plotW}px`
      hl.style.height = `${RH}px`
      hl.style.display = 'block'
    }
    const chip = chipRef.current
    if (chip) {
      // Value at cursor Y — inverse of yOf, from the scale the draw loop
      // published this frame; clamped to the pane's range like the chart
      // clamps cursor mcap to the visible rows.
      const { mx, mid } = yScaleRef.current
      const v = Math.max(-mx, Math.min(mx, ((mid - y) / (mid - 6)) * mx))
      let textEl = chip.firstElementChild
      if (!textEl || textEl.className !== 'price-label-text') {
        chip.textContent = ''
        textEl = document.createElement('span')
        textEl.className = 'price-label-text'
        chip.appendChild(textEl)
      }
      textEl.textContent = fmtDelta(v)
      chip.style.top = `${y}px`
      chip.style.left = `${plotW}px`
      chip.style.width = `${rect.width - plotW}px`   // spans the gutter = chart's "match y-axis width"
      chip.style.display = 'flex'
    }
  }
  const onPointerLeave = () => {
    // If the cursor moved onto the main canvas, its handlers re-set next
    // frame (one-frame flicker — CVD accepts the same, fine in practice).
    selfCursorXRef.current = -1   // hand the line back to mouseXRef
    mouseXRef.current = -1
    if (onCursorXChange) onCursorXChange(-1)
    else placeCrosshairs(mouseXRef, -1)
    hideHorizontal()
  }

  return (
    <div ref={wrapRef} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave}
      style={{ position: 'relative', height, flexShrink: 0, borderTop: '1px solid #1a1d24', background: '#0b0d12', touchAction: 'none', cursor: 'crosshair' }}>
      <canvas ref={plotRef} style={{ display: 'block' }} />
      {/* Vertical crosshair (DOM, same-frame as the chart's), clipped to the
          plot so it never runs over the axis gutter. */}
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: axisWidth || FIXED_OVERLAY_WIDTH, overflow: 'hidden', pointerEvents: 'none' }}>
        <div ref={xhairRef} className="chart-crosshair" style={CROSSHAIR_PANE_STYLE} />
      </div>
      {/* Horizontal crosshair — the chart's OWN elements. Same classNames
          (.chart-h-highlight / .price-label / .price-label-text) so the
          live stylesheet styles both panes identically; translateY(-50%)
          centers on the cursor exactly like the chart's inline style. */}
      <div ref={hlRef} className="chart-h-highlight" style={{ display: 'none', transform: 'translateY(-50%)' }} />
      <div ref={chipRef} className="price-label" style={{ display: 'none', transform: 'translateY(-50%)' }} />
      <div style={{ position: 'absolute', top: 2, left: 6, font: '600 9px "Nova Square", system-ui, sans-serif', color: '#5a668c', letterSpacing: '1px', pointerEvents: 'none' }}>
        DELTA VOLUME PRO {config.deltaMode === 'rolling' ? `· Σ${config.rollingLen}` : ''} {config.unitMode === 'usd' ? '· $' : ''}
      </div>

      {/* X-scale stops — bottom centre, dimmed until reached for. MOUSE
          enter/leave, not pointer: this pane's own onPointerMove drives the
          shared crosshair and the chart takes pointer capture on drag, which
          retargets pointer events away from the buttons mid-click. Pointer
          events are stopped here so pressing a stop cannot also start a pan
          or move the crosshair. */}
      <div
        style={{
          position: 'absolute', left: '50%', bottom: 3, transform: 'translateX(-50%)',
          display: 'flex', alignItems: 'center', gap: 2,
          padding: '2px 4px', borderRadius: 4,
          background: 'rgba(12,14,18,0.72)', border: '1px solid #23262d',
          opacity: scaleUiHot ? 1 : 0.28, transition: 'opacity 0.15s', zIndex: 3,
        }}
        onMouseEnter={() => setScaleUiHot(true)}
        onMouseLeave={() => setScaleUiHot(false)}
        onPointerDown={(e) => { e.stopPropagation(); setScaleUiHot(true) }}
        onPointerMove={(e) => e.stopPropagation()}
      >
        {DVP_SCALES.map(v => (
          <button
            key={v}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); setXScale(v) }}
            title={v === 1 ? 'Match the chart' : `${v}× more history in the same width`}
            style={{
              background: v === xScale ? '#26a69a22' : 'transparent',
              border: '1px solid ' + (v === xScale ? '#26a69a66' : 'transparent'),
              color: v === xScale ? '#26a69a' : '#6b7280',
              font: '600 10px "Nova Square", system-ui, sans-serif',
              padding: '1px 5px', borderRadius: 3, cursor: 'pointer', lineHeight: 1.4,
            }}
          >
            {v}×
          </button>
        ))}
      </div>
    </div>
  )
}